import { Box, Button, HStack, Icon, useBreakpointValue, VStack } from '@chakra-ui/react'
import { useContext, useEffect, useRef, useState } from 'react'
import { RiAlertFill, RiNotificationLine, RiSearchLine } from 'react-icons/ri'
import { Link } from 'react-router-dom'
import { ConfigurationContext } from '../../context/ConfigurationContext'
import { SearchContext } from '../../context/SearchContext'
import { Notifications } from '../../Interfaces/notifications'

const STOCK_COL_WIDTH = '120px'

type ItemProps = {
  notification: Notifications
  isFirst: boolean
}

function NotificationItem({ notification, isFirst }: ItemProps) {
  return (
    <Box
      // linha inteira clicável
      as={Link}
      to={`/raw-materials/edit/${notification.id}`}
      width="100%"
      display="grid"
      gridTemplateColumns={`minmax(0,1fr) ${STOCK_COL_WIDTH}`} // nome | estoque
      alignItems="stretch"
      position="relative"
      color="white"
      _hover={{ backgroundColor: 'pink.400' }}
      // linha horizontal sutil (não no primeiro item)
      borderTop={isFirst ? 'none' : '1px solid'}
      borderColor={isFirst ? 'transparent' : 'whiteAlpha.400'}>
      {/* Barra vertical fixa exatamente na fronteira das colunas */}
      <Box
        position="absolute"
        left={`calc(100% - ${STOCK_COL_WIDTH})`}
        top="0"
        bottom="0"
        width="1px"
        backgroundColor="white"
      />

      {/* Nome (quebra natural em múltiplas linhas) */}
      <Box fontSize="14px" py="10px" pl="14px" pr="12px" textAlign="left" whiteSpace="normal" wordBreak="break-word">
        {notification.name}
      </Box>

      {/* Estoque (coluna fixa à direita) */}
      <Box
        fontSize="14px"
        px="14px"
        py="10px"
        textAlign="right"
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        minH="34px">
        {notification.amount} - {notification.measurement_unit}
      </Box>
    </Box>
  )
}

export function NotificationsNav() {
  const { isShortScreenOpen, setIsShortScreenOpen } = useContext(SearchContext)
  const [showNotification, setShowNotification] = useState(false)
  const notificationRef = useRef<HTMLButtonElement | null>(null)

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  })

  const openSearch = () => setIsShortScreenOpen(!isShortScreenOpen)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setShowNotification(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const { notifications }: { notifications: Notifications[] } = useContext(ConfigurationContext)

  return (
    <HStack
      spacing={['6', '8']}
      mx={['6', '8']}
      pr={['6', '8']}
      py="1"
      color="gray.300"
      borderRightWidth={1}
      borderColor="gray.700">
      {!isWideVersion && (
        <Box as="button" onClick={openSearch}>
          <Icon _hover={{ color: 'pink.A400' }} as={RiSearchLine} fontSize="20" />
        </Box>
      )}

      <Button
        position="relative"
        minWidth="auto"
        variant="unstyled"
        onClick={() => setShowNotification(!showNotification)}
        ref={notificationRef}>
        <Icon as={RiNotificationLine} fontSize="20" />

        {notifications.length > 0 && (
          <Box
            position="absolute"
            top="4px"
            right="-6px"
            backgroundColor="red.400"
            color="white"
            width="15px"
            height="15px"
            borderRadius="15px"
            fontSize="12px"
            textAlign="center"
            justifyContent="center"
            lineHeight="15px"
            minWidth="auto">
            {notifications.length}
          </Box>
        )}

        {showNotification && (
          <Box
            backgroundColor="purple.500"
            position="absolute"
            top="100%"
            right="0"
            mt="10px"
            zIndex={9}
            minWidth="320px"
            maxWidth="420px"
            maxHeight="420px"
            borderRadius="8px"
            boxShadow="lg"
            overflow="hidden">
            <Box
              p="10px"
              borderBottom="1px"
              borderColor="white"
              color="white"
              backgroundColor="pink.400"
              borderTopRadius="8px">
              <Icon as={RiAlertFill} /> Estoque baixo ({notifications?.length || 0})
            </Box>

            <VStack spacing="0" py="0" maxHeight="360px" overflowY="auto">
              {notifications && notifications.length > 0 ? (
                notifications.map((n, i) => <NotificationItem key={n.id} notification={n} isFirst={i === 0} />)
              ) : (
                <Box p="12px" color="white">
                  Nenhuma notificação
                </Box>
              )}
            </VStack>
          </Box>
        )}
      </Button>
    </HStack>
  )
}
